// Dimension manager (DIM-1). PURE (ENG-1).
//
// Holds the loaded worlds keyed by dimension id; exactly one is "active" (rendered
// + fully ticked). Inactive dimensions are paused in v1 (documented divergence:
// no forced portal-chunk ticking) — they retain their in-memory state and resume
// when re-entered. Owns the per-dimension portal cache and the search-or-build
// linking that makes a round trip land on the same portal (DIM-2, E-11).
import { PORTAL } from '../config';
import type { World } from '../engine/world';
import {
  buildPortal, findNearestPortal, mapPosition, NETHER_SPAWN_CEIL,
  type DimId, type PortalAccess, type PortalBlocks, type Vec3,
} from './portal';
import { buildEndPlatform } from '../gen/end/terrain';

/** Outcome of stepping through a portal: destination dim + arrival feet position. */
export interface TravelResult {
  dim: DimId;
  arrival: Vec3;
  /** Anchor of a newly built destination portal (for relight/remesh), or null. */
  built: Vec3 | null;
}

/** A World exposed to the portal code as a block accessor. */
export function portalAccessOf(world: World): PortalAccess {
  return {
    get: (x, y, z) => world.getBlockState(x, y, z),
    set: (x, y, z, s) => world.setBlockState(x, y, z, s),
    isSolid: (x, y, z) => world.isSolid(x, y, z),
  };
}

export class DimensionManager {
  private readonly worlds = new Map<DimId, World>();
  private readonly portalCache = new Map<DimId, Vec3[]>();
  active: DimId = 'overworld';

  constructor(
    private readonly makeWorld: (dim: DimId) => World,
    private readonly blocks: PortalBlocks,
  ) {}

  /** The World for a dimension, created lazily on first entry. */
  world(dim: DimId): World {
    let w = this.worlds.get(dim);
    if (!w) { w = this.makeWorld(dim); this.worlds.set(dim, w); }
    return w;
  }

  get activeWorld(): World { return this.world(this.active); }
  loaded(dim: DimId): boolean { return this.worlds.has(dim); }
  loadedWorlds(): IterableIterator<World> { return this.worlds.values(); }

  /** Record a portal's anchor cell so return trips can link to it (DIM-2). */
  registerPortal(dim: DimId, cell: Vec3): void {
    const list = this.portalCache.get(dim) ?? [];
    if (!list.some((p) => p[0] === cell[0] && p[1] === cell[1] && p[2] === cell[2])) list.push(cell);
    this.portalCache.set(dim, list);
  }

  portalsIn(dim: DimId): readonly Vec3[] { return this.portalCache.get(dim) ?? []; }

  /**
   * Resolve where a player stepping into an END portal arrives (DIM-3). The End is
   * not coordinate-mapped like the nether: going in always lands on the fixed
   * obsidian arrival platform (built if the void is all that's there), and coming
   * out returns to the overworld spawn the caller names — so this takes no source
   * position at all.
   */
  travelEnd(from: DimId, overworldReturn: Vec3): TravelResult {
    if (from === 'end') {
      const world = this.world('overworld');
      const [rx, ry, rz] = overworldReturn;
      // Land on the surface at the return column, not inside whatever is there.
      const y = Math.max(ry, world.terrainHeight(Math.floor(rx), Math.floor(rz)) + 1);
      return { dim: 'overworld', arrival: [rx, y, rz], built: null };
    }
    const world = this.world('end');
    const { arrival } = buildEndPlatform(portalAccessOf(world), this.blocks.obsidian);
    return { dim: 'end', arrival, built: null };
  }

  /**
   * Resolve where a player entering a portal at (x,y,z) in `from` arrives:
   * map the coordinates (⅛/×8), search the destination cache within the per-side
   * radius for an existing portal, else build a fresh 4×5 frame on a safe pocket.
   */
  travel(from: DimId, x: number, y: number, z: number): TravelResult {
    const to: DimId = from === 'overworld' ? 'nether' : 'overworld';
    const [mx, my0, mz] = mapPosition(x, y, z, from, to);
    const destWorld = this.world(to);
    const radius = to === 'overworld' ? PORTAL.SEARCH_OVERWORLD : PORTAL.SEARCH_NETHER;

    const existing = findNearestPortal(this.portalsIn(to), mx, my0, mz, radius);
    if (existing) {
      return { dim: to, arrival: [existing[0] + 0.5, existing[1], existing[2] + 0.5], built: null };
    }

    const buildY = this.findBuildY(destWorld, to, mx, my0, mz);
    const { frame, arrival } = buildPortal(portalAccessOf(destWorld), this.blocks, mx, buildY, mz);
    const anchor: Vec3 = [frame.a0, frame.y0, frame.fixed];
    this.registerPortal(to, anchor);
    return { dim: to, arrival, built: anchor };
  }

  /** Pick a safe build Y: a solid floor with 3-tall air headroom, scanning down
   *  from the ceiling (nether) or from the mapped point (overworld). E-11 carves
   *  a pocket regardless, so this only needs to be "reasonable". */
  private findBuildY(world: World, dim: DimId, x: number, my: number, z: number): number {
    const top = dim === 'nether' ? NETHER_SPAWN_CEIL : Math.min(my + 8, 200);
    const bottom = dim === 'nether' ? 5 : -40;
    for (let y = top; y > bottom; y--) {
      if (world.isSolid(x, y, z) && !world.isSolid(x, y + 1, z) && !world.isSolid(x, y + 2, z) && !world.isSolid(x, y + 3, z)) {
        return y + 1;
      }
    }
    return dim === 'nether' ? 64 : Math.max(my, world.terrainHeight(x, z));
  }
}
